import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { Enseignant } from '@app/models/enseignant';
import { Cours } from '@app/models/lasido';
import { PointageEnseignantConfig } from '@app/models/pointage-enseignant';
import { EnseignantService } from '@app/services/enseignant.service';
import { BaseConfigService, UserService } from '@app/services';
import { SharedModule } from '@app/modules/shared.module';
import { PublicModule } from '@app/modules/public.module';
import { DepartmentTreeComponent } from './department-tree/department-tree.component';
import { forkJoin } from 'rxjs';

const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

@Component({
  selector: 'app-enseignant-info',
  imports: [MatIconModule, MatCardModule, DepartmentTreeComponent, SharedModule, PublicModule],
  templateUrl: './enseignant-info.component.html',
  styleUrl: './enseignant-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EnseignantInfoComponent implements OnInit {
  private enseignantService = inject(EnseignantService);
  private userService = inject(UserService);
  private configService = inject(BaseConfigService);

  private adulte = toSignal(this.userService.currentAdulte$);

  idEnseignant = this.userService.currentUser.idEnseignant;

  enseignant = signal<Enseignant | null>(null);
  isLoading = signal(true);

  planningConfig = signal<PointageEnseignantConfig | null>(null);
  coursList = signal<Cours[]>([]);

  nom = computed(() => this.adulte()?.nom ?? '');
  prenom = computed(() => this.adulte()?.prenom ?? '');

  availabilityDesc = computed(() => {
    const e = this.enseignant();
    if (!e) return '';
    const active = Object.keys(e.availability ?? {})
      .filter(k => e.availability![+k]?.length)
      .map(k => DAYS_SHORT[+k])
      .join(', ');
    const theorique = (e.dureeHebdomadaireTheorique ?? 0) > 1 ? 'théoriques' : 'théorique';
    return `${e.dureeHebdomadaireTheorique} h ${theorique} / semaine · ${active || '—'}`;
  });

  ngOnInit(): void {
    forkJoin({
      enseignant: this.enseignantService.getEnseignantRenseignements(this.idEnseignant),
      config: this.configService.get<PointageEnseignantConfig>('enseignant-pointage'),
      coursList: this.enseignantService.getCoursList(this.idEnseignant),
    }).subscribe({
      next: ({ enseignant, config, coursList }) => {
        this.enseignant.set(enseignant);
        this.planningConfig.set(config);
        this.coursList.set(coursList);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }
}
